'use client';

/**
 * Redirects authenticated users whose tenant skipped the post-signup
 * wizard to /admin/welcome. OAuth signups land in /admin first; this
 * gate then bounces them once.
 *
 * The check is cheap: one cached fetch per mount. It runs after auth
 * is resolved (sits inside AuthGuard + ActiveTenantProvider in the
 * layout) so the API call always has a session. If the API errors we
 * fail open — better to risk one over-rendered dashboard than to
 * block sign-in on a transient backend hiccup.
 */
import { useEffect } from 'react';
import { usePathname, useRouter } from 'next/navigation';

let hasCheckedThisSession = false;
// Module-level in-flight promise so React 19 strict-mode's double
// effect-mount doesn't fire two network calls before the flag is set.
let inflightOnboardingStatus: Promise<Response> | null = null;
export function fetchOnboardingStatus(): Promise<Response> {
  if (inflightOnboardingStatus) {
    return inflightOnboardingStatus.then((r) => r.clone());
  }
  const p = fetch('/api/onboarding/status', { cache: 'no-store' }).finally(() => {
    inflightOnboardingStatus = null;
  });
  inflightOnboardingStatus = p;
  return p.then((r) => r.clone());
}

export function OnboardingGate() {
  const router = useRouter();
  const pathname = usePathname();

  useEffect(() => {
    if (hasCheckedThisSession) return;
    if (pathname === '/admin/welcome') return;

    let cancelled = false;
    (async () => {
      try {
        const res = await fetchOnboardingStatus();
        if (!res.ok) return;
        const data = (await res.json()) as { needsOnboarding: boolean };
        if (cancelled) return;
        hasCheckedThisSession = true;
        if (data.needsOnboarding) {
          router.replace('/admin/welcome');
        }
      } catch {
        /* fail-open: skip gate on transient errors */
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [pathname, router]);

  return null;
}
